import React from "react";
import { Disc3, Plus, Users, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { ArtThumb } from "@/components/riptag/TrackTable";

export const AlbumStrip = ({ albums, unassigned, totalTracks, filter, onFilter, onCreate, onEdit, onDelete }) => {
  const card = (active) =>
    `shrink-0 text-left rounded-lg border p-2.5 w-[200px] transition ${
      active ? "border-brand-amber bg-brand-amber/10" : "border-line bg-bg-surface hover:border-brand-amber/50"}`;

  return (
    <div data-testid="rip-album-strip">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-muted flex items-center gap-1.5">
          <Disc3 className="h-3.5 w-3.5" /> Albums in this session ({albums.length})
        </div>
        <button onClick={onCreate} data-testid="rip-new-album-btn"
          className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:border-brand-amber/60 transition">
          <Plus className="h-3 w-3" /> New album
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1.5">
        <button onClick={() => onFilter("all")} className={card(filter === "all")} data-testid="rip-album-all">
          <div className="text-xs font-bold">All tracks</div>
          <div className="text-[10px] font-mono text-ink-muted mt-0.5">{totalTracks} tracks · every album</div>
        </button>

        {albums.map((a) => (
          <div key={a.album_id} className={`relative ${card(filter === a.album_id)}`}>
            <button onClick={() => onFilter(a.album_id)} data-testid={`rip-album-card-${a.album_id}`}
              className="flex items-start gap-2 w-full text-left">
              <ArtThumb url={a.art_url} size="w-10 h-10" testId={`rip-album-art-${a.album_id}`} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold truncate">{a.title}</div>
                <div className="text-[10px] text-ink-muted font-mono truncate">
                  {a.is_compilation ? "Various Artists" : (a.artist || "Unknown")}{a.year ? ` · ${a.year}` : ""}
                </div>
                <div className="text-[10px] text-ink-muted font-mono mt-0.5 flex items-center gap-1.5">
                  {a.track_count} trk
                  {a.is_compilation && <span className="inline-flex items-center gap-0.5 text-brand-cyan"><Users className="h-2.5 w-2.5" />VA</span>}
                  {a.mb_release_id && <span className="text-emerald-300">MB✓</span>}
                  {a.dup_count > 0 && <span className="inline-flex items-center gap-0.5 text-amber-300"><AlertTriangle className="h-2.5 w-2.5" />{a.dup_count}</span>}
                </div>
              </div>
            </button>
            {filter === a.album_id && (
              <div className="absolute top-1 right-1 flex gap-0.5">
                <button onClick={() => onEdit(a)} data-testid={`rip-album-edit-${a.album_id}`}
                  className="rounded p-1 text-ink-muted hover:text-brand-amber" title="Edit album">
                  <Pencil className="h-3 w-3" />
                </button>
                <button onClick={() => onDelete(a)} data-testid={`rip-album-delete-${a.album_id}`}
                  className="rounded p-1 text-ink-muted hover:text-red-400" title="Ungroup album (keeps tracks)">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        ))}

        {unassigned > 0 && (
          <button onClick={() => onFilter("unassigned")} className={card(filter === "unassigned")} data-testid="rip-album-unassigned">
            <div className="text-xs font-bold text-amber-300">Unassigned</div>
            <div className="text-[10px] font-mono text-ink-muted mt-0.5">{unassigned} tracks without an album</div>
          </button>
        )}
      </div>
    </div>
  );
};
